import { useState } from "react";
import API from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

/**
 * Apple App Store 5.1.1(v) compliance — in-app account deletion.
 * Shows a confirmation dialog with password + optional reason, hits the backend
 * POST /auth/delete-account, and signs the user out on success.
 */
export default function DeleteAccountCard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user?.is_super_admin) return null;  // super admin can't self-delete
  if (user?.role === "admin") return null; // providers managed by team tools

  const onConfirm = async () => {
    setBusy(true); setErr("");
    try {
      await API.post("/auth/delete-account", { password, reason: reason.trim() });
      toast({
        title: "Account deleted",
        description: "Your portal access has been revoked. Your clinical record is retained per law.",
        duration: 5000,
      });
      setOpen(false);
      await logout?.();
      navigate("/login", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not delete account");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-red-200/60 rounded-xl p-4 space-y-2" data-testid="delete-account-card">
      <h3 className="font-semibold text-sm flex items-center gap-2 text-red-700">
        <Trash2 className="w-4 h-4" /> Delete my account
      </h3>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Permanently remove your portal access. Your clinical records stay on file with your
        provider for as long as state law requires, but you'll no longer be able to sign in.
      </p>
      <Button
        variant="outline"
        size="sm"
        className="text-red-700 border-red-200 hover:bg-red-50 rounded-full"
        onClick={() => setOpen(true)}
        data-testid="delete-account-open-button"
      >
        Delete account…
      </Button>

      <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4" /> Delete your account?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. You'll be signed out immediately. Your treatment record
              stays with your care team, but your app login, messages, journals, and mood check-ins
              will no longer be accessible to you.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Confirm your password *</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-9 mt-1"
                data-testid="delete-account-password"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="mt-1 text-sm"
                maxLength={500}
                data-testid="delete-account-reason"
                placeholder="Help us improve — what's prompting this?"
              />
            </div>
            {err && <p className="text-xs text-red-600" data-testid="delete-account-error">{err}</p>}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirm}
              disabled={busy || password.length < 4}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-account-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Permanently delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
